import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft,
  Key,
  Plus,
  Store,
  Eye,
  EyeOff,
  X,
  Wallet,
  Percent,
  Building2,
  Bell,
  Mail,
  Clock3,
  Briefcase,
  Pencil,
  DollarSign,
  Skull,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { PhoneInput } from '../../ui/PhoneInput';
import Badge from '../../ui/Badge';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import { useTimezone, formatInTimezone } from '@kioskgaming/ui';
import { LoadingSpinner } from '../../ui/Loading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/Table';
import {
  useAdminAgentDetail,
  useCreateAdminAgentShop,
  usePatchAdminAgentPassword,
  usePatchAdminAgentRates,
  usePatchAdminAgentPopupPermissions,
  usePatchAdminShopPopupPermissions,
  usePatchAdminAgentPlayerWithdrawalAutoApprove,
  useCostReport,
  useUsdWalletSummary,
  useSuperAgentDetail,
  QUERY_KEYS,
} from '../../../hooks/useApi';
import { usePermission, useRole } from '../../../hooks/useAuth';
import { useBreadcrumbLabel } from '../../../contexts/BreadcrumbLabelContext';
import type { CdnShop } from '../../../types';
import { costAsPercent, percentInputToRate } from './agentCdnUtils';
import { AgentPaymentTransactionsPanel } from './AgentPaymentTransactionsPanel';
import { AgentWalletTransferPanel } from './AgentWalletTransferPanel';
import FinanceCorrectionDebitModal from '../../finance/FinanceCorrectionDebitModal';
import OwnerWalletPnlCards from '../wallets/OwnerWalletPnlCards';
import EntityHierarchyChart from './EntityHierarchyChart';
import AdminUpdateContactModal from '../../shared/AdminUpdateContactModal';
import AdminUpdateUsernameModal from '../../shared/AdminUpdateUsernameModal';
import AdminPlayerWithdrawalAutoApproveCard from '../../shared/AdminPlayerWithdrawalAutoApproveCard';
import { cn, formatCurrency } from '../../../utils';
import { getCdnShopUsernameError } from '../../../utils/username';
import TerminateAgentModal from './TerminateAgentModal';

const PASSWORD_MIN = 8;
type AgentDetailTab = 'overview' | 'hierarchy' | 'finance' | 'shops' | 'settings';
const AGENT_DETAIL_TABS: AgentDetailTab[] = ['overview', 'hierarchy', 'finance', 'shops', 'settings'];

const isAgentDetailTab = (value: string | null): value is AgentDetailTab =>
  !!value && AGENT_DETAIL_TABS.includes(value as AgentDetailTab);

type FinanceSection = 'transactions' | 'wallet' | 'cost-breakdown';
const FINANCE_SECTIONS: FinanceSection[] = ['transactions', 'wallet', 'cost-breakdown'];
const isFinanceSection = (value: string | null): value is FinanceSection =>
  !!value && FINANCE_SECTIONS.includes(value as FinanceSection);

const getStatusToneClass = (status?: string): string => {
  if (status === 'active') return 'bg-green-100 text-green-700 border-green-200';
  if (status === 'suspended') return 'bg-amber-100 text-amber-800 border-amber-200';
  return 'bg-red-100 text-red-700 border-red-200';
};

const formatAgentName = (raw?: string): string => {
  if (!raw) return '';
  return raw.replace(/([A-Z])/g, ' $1').trim();
};

const getInitials = (name?: string): string => {
  if (!name) return '--';
  const normalized = name.trim();
  if (!normalized) return '--';
  const words = normalized.split(/\s+/).filter(Boolean);
  const initials = words
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
  if (initials.length >= 2) return initials.slice(0, 2);
  return normalized.slice(0, 2).toUpperCase();
};

const AgentDetailPage: React.FC = () => {
  const { timezone } = useTimezone();
  const { id: agentId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const canManage = usePermission('agentManagement');
  // KIOSK-720: Adjust Wallet is a write action — operation_admin/platform_admin may only VIEW wallets.
  const canAdjustWalletRole = useRole(['system_admin', 'super_admin', 'finance_admin']);
  const canFinanceCorrect = usePermission('walletManagement') && canAdjustWalletRole;
  const canUpdateContact = useRole(['system_admin', 'super_admin']);
  const canManageCredentials = canUpdateContact;
  const canManagePopupPermissions = usePermission('agentPopupPermissionManagement');
  const { data, isLoading, error } = useAdminAgentDetail(agentId, canManage);
  const agentForParent = data?.data?.agent;
  const superAgentId = agentForParent?.superAgentId ?? undefined;
  const {
    data: superAgentDetail,
    isLoading: superAgentLoading,
  } = useSuperAgentDetail(superAgentId, canManage && !!superAgentId);
  const {
    data: usdWalletSummary,
    isLoading: usdWalletLoading,
    error: usdWalletError,
  } = useUsdWalletSummary(
    { ownerType: 'agent', ownerId: agentId ?? '', currency: 'USD' },
    canManage && !!agentId,
  );
  const createShop = useCreateAdminAgentShop();
  const patchAgentPassword = usePatchAdminAgentPassword();
  const patchAgentRates = usePatchAdminAgentRates();
  const patchAgentPopup = usePatchAdminAgentPopupPermissions();
  const patchShopPopup = usePatchAdminShopPopupPermissions();
  const patchPlayerAutoApprove = usePatchAdminAgentPlayerWithdrawalAutoApprove();

  const [agentCostPercentInput, setAgentCostPercentInput] = useState('');

  const [shopModalOpen, setShopModalOpen] = useState(false);
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [usernameModalOpen, setUsernameModalOpen] = useState(false);
  const [costRateModalOpen, setCostRateModalOpen] = useState(false);
  const [terminateModalOpen, setTerminateModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [agentPassword, setAgentPassword] = useState('');
  const [agentPasswordConfirm, setAgentPasswordConfirm] = useState('');
  const [showAgentPasswordReset, setShowAgentPasswordReset] = useState(false);
  const [shopForm, setShopForm] = useState({
    name: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    cost_percent: '0',
  });
  const [reportFrom, setReportFrom] = useState('');
  const [reportTo, setReportTo] = useState('');
  const [reportEventType, setReportEventType] = useState('');
  const [activeTab, setActiveTab] = useState<AgentDetailTab>(() => {
    const tabFromUrl = searchParams.get('tab');
    return isAgentDetailTab(tabFromUrl) ? tabFromUrl : 'overview';
  });
  const restrictedTabs: AgentDetailTab[] = ['finance', 'settings'];

  const agent = data?.data?.agent;
  useBreadcrumbLabel(agent?.name);
  const shops = useMemo(() => data?.data?.shops ?? [], [data]);
  const agentWallet = data?.data?.agent_wallet;
  const costReport = useCostReport(!!agentId && canManage, {
    agent_id: agentId,
    from: reportFrom || undefined,
    to: reportTo || undefined,
    event_type: (reportEventType as 'shop_direct_topup' | 'user_direct_topup') || undefined,
    page: 1,
    limit: 100,
  });

  const agentCostRate = useMemo(() => {
    const r = agent?.costRate;
    const n = typeof r === 'string' ? parseFloat(r) : Number(r);
    if (!Number.isFinite(n)) return null;
    return Math.min(1, Math.max(0, n));
  }, [agent?.costRate]);

  const financeSection: FinanceSection = useMemo(() => {
    const sectionFromUrl = searchParams.get('section');
    if (!isFinanceSection(sectionFromUrl)) return 'transactions';
    return sectionFromUrl;
  }, [searchParams]);

  const setFinanceSection = (section: FinanceSection) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', 'finance');
    nextParams.set('section', section);
    nextParams.delete('tx');
    setSearchParams(nextParams, { replace: true });
  };

  const resetShopForm = () => {
    setShopForm({
      name: '',
      username: '',
      email: '',
      phone: '',
      password: '',
      cost_percent: '0',
    });
    setShowPassword(false);
  };

  const switchTab = (tab: AgentDetailTab) => {
    setActiveTab(tab);
    const nextParams = new URLSearchParams(searchParams);
    if (tab === 'overview') {
      nextParams.delete('tab');
      nextParams.delete('section');
      nextParams.delete('tx');
    } else {
      nextParams.set('tab', tab);
      if (tab !== 'finance') {
        nextParams.delete('section');
        nextParams.delete('tx');
      } else if (!isFinanceSection(nextParams.get('section'))) {
        nextParams.set('section', 'transactions');
      }
    }
    setSearchParams(nextParams, { replace: true });
  };

  const handleCreateShop = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agentId || !canManage) return;
    if (shopForm.password.length < PASSWORD_MIN) return;
    const usernameError = getCdnShopUsernameError(shopForm.username);
    if (usernameError) {
      toast.error(usernameError);
      return;
    }
    const username = shopForm.username.trim();
    await createShop.mutateAsync({
      agentId,
      data: {
        name: shopForm.name.trim(),
        ...(username ? { username } : {}),
        email: shopForm.email.trim(),
        phone: shopForm.phone.trim() || undefined,
        password: shopForm.password,
        cost_rate: percentInputToRate(shopForm.cost_percent),
      },
    });
    resetShopForm();
    setShopModalOpen(false);
    switchTab('shops');
  };

  const closePasswordModal = () => {
    if (patchAgentPassword.isLoading) return;
    setPasswordModalOpen(false);
    setAgentPassword('');
    setAgentPasswordConfirm('');
    setShowAgentPasswordReset(false);
  };

  const handleAgentPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agentId || !canManage) return;
    if (agentPassword.length < PASSWORD_MIN) {
      toast.error(`Password must be at least ${PASSWORD_MIN} characters`);
      return;
    }
    if (agentPassword !== agentPasswordConfirm) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      await patchAgentPassword.mutateAsync({ agentId, password: agentPassword });
      setAgentPassword('');
      setAgentPasswordConfirm('');
      setShowAgentPasswordReset(false);
      setPasswordModalOpen(false);
    } catch {
      // Toast from usePatchAdminAgentPassword onError
    }
  };

  const handleAgentCostRateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agentId || !canManage || patchAgentRates.isLoading) return;
    const value = Number.parseFloat(agentCostPercentInput);
    if (!Number.isFinite(value) || value < 0 || value > 100) {
      toast.error('Cost rate must be between 0 and 100');
      return;
    }
    try {
      await patchAgentRates.mutateAsync({
        agentId,
        cost_rate: value / 100,
      });
      setCostRateModalOpen(false);
    } catch {
      // Toast from usePatchAdminAgentRates onError
    }
  };

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (isAgentDetailTab(tabFromUrl) && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
    if (!tabFromUrl && activeTab !== 'overview') {
      setActiveTab('overview');
    }
  }, [searchParams, activeTab]);

  useEffect(() => {
    if (activeTab !== 'finance') return;
    const section = searchParams.get('section');
    if (section === 'cash-funding' || !isFinanceSection(section)) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('tab', 'finance');
      nextParams.set('section', 'transactions');
      setSearchParams(nextParams, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams]);

  useEffect(() => {
    if (agentCostRate == null) {
      setAgentCostPercentInput('');
      return;
    }
    setAgentCostPercentInput((agentCostRate * 100).toFixed(2).replace(/\.00$/, ''));
  }, [agentCostRate]);

  const handleTabChange = (tab: AgentDetailTab) => switchTab(tab);

  if (!agentId) {
    return (
      <div className="p-6 text-muted-foreground">Invalid agent.</div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={() => navigate('/agents')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to agents
        </Button>
        <p className="text-destructive">Failed to load agent or agent not found.</p>
      </div>
    );
  }

  const statusTone = getStatusToneClass(agent.status);
  const formattedAgentName = formatAgentName(agent.name || '');
  const agentInitials = getInitials(agent.name || '');
  const isAgentActive = agent.status === 'active';
  const activeShopsCount = shops.filter((shop) => shop.status === 'active').length;
  const tabs: Array<{ value: AgentDetailTab; label: string; count?: number }> = [
    { value: 'overview', label: 'Overview' },
    { value: 'hierarchy', label: 'Hierarchy' },
    { value: 'finance', label: 'Finance' },
    { value: 'shops', label: 'Shops', count: shops.length },
    { value: 'settings', label: 'Settings' },
  ];

  return (
    <div className="space-y-6">
      <Card className="border-border/80">
        <CardContent className="space-y-4 pt-6">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" className="-ml-2" onClick={() => navigate('/agents')}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          </div>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-700 text-base font-medium">
                {agentInitials}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="truncate text-[18px] font-medium tracking-tight">{formattedAgentName || agent.name}</h1>
                  <span
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium',
                      isAgentActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'
                    )}
                  >
                    <span className={cn('h-1.5 w-1.5 rounded-full', isAgentActive ? 'bg-green-600' : 'bg-red-600')} />
                    {isAgentActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {agent.email ? (
                    <span className="inline-flex items-center gap-1 rounded-md border border-border/80 bg-muted/70 px-2 py-0.5 text-[11px] text-muted-foreground">
                      <Mail className="h-3 w-3" />
                      {agent.email}
                    </span>
                  ) : null}
                  {agentCostRate != null ? (
                    <span className="inline-flex items-center gap-1 rounded-md border border-border/80 bg-muted/70 px-2 py-0.5 text-[11px] text-muted-foreground">
                      <Clock3 className="h-3 w-3" />
                      Cost rate {(agentCostRate * 100).toFixed(0)}%
                    </span>
                  ) : null}
                  {shops.length > 0 ? (
                    <span className="inline-flex items-center gap-1 rounded-md border border-border/80 bg-muted/70 px-2 py-0.5 text-[11px] text-muted-foreground">
                      <Briefcase className="h-3 w-3" />
                      {shops.length} shops
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
            {canManage && canFinanceCorrect && agent?.status !== 'terminated' ? (
              <div className="shrink-0 border-t border-border/60 pt-4 sm:ml-auto sm:border-t-0 sm:border-l sm:pl-4 sm:pt-0">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-destructive/40 text-destructive w-full sm:w-auto"
                  onClick={() => setCorrectionOpen(true)}
                >
                  Debit correction…
                </Button>
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => (
              <Button
                key={tab.value}
                size="sm"
                variant={activeTab === tab.value ? 'primary' : 'outline'}
                onClick={() => handleTabChange(tab.value)}
                className="gap-2"
              >
                <span>{tab.label}</span>
                {typeof tab.count === 'number' && (
                  <span
                    className={cn(
                      'inline-flex min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold',
                      activeTab === tab.value ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted text-foreground'
                    )}
                  >
                    {tab.count}
                  </span>
                )}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {!canManage && restrictedTabs.includes(activeTab) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Access required</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              You do not have permission to access the <span className="font-medium">{activeTab}</span> tab.
            </p>
          </CardContent>
        </Card>
      )}

      {activeTab === 'overview' && (
        <>
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardContent className="pt-5 space-y-2">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Wallet className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm font-medium">Wallet balance</span>
                </div>
                <p className="text-2xl font-semibold tabular-nums">{agentWallet?.balance ?? '—'}</p>
                <p className="text-xs text-muted-foreground">CDN credits</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5 space-y-2">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <DollarSign className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm font-medium">USD wallet</span>
                </div>
                {usdWalletLoading ? (
                  <div className="flex justify-center py-3">
                    <LoadingSpinner />
                  </div>
                ) : usdWalletError ? (
                  <p className="text-sm text-destructive">Could not load USD balance.</p>
                ) : (
                  <>
                    <p className="text-2xl font-semibold tabular-nums">
                      {formatCurrency(Number(usdWalletSummary?.balanceNet ?? 0))}{' '}
                      <span className="text-sm font-normal text-muted-foreground">net</span>
                    </p>
                    {Number(usdWalletSummary?.balanceGross ?? 0) !==
                    Number(usdWalletSummary?.balanceNet ?? 0) ? (
                      <p className="text-xs text-muted-foreground tabular-nums">
                        Gross {formatCurrency(Number(usdWalletSummary?.balanceGross ?? 0))}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground">Online settlement balance</p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardContent className="pt-5 space-y-2">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Percent className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium">Cost rate</span>
                </div>
                <p className="text-2xl font-semibold">{costAsPercent(agent)}</p>
                <p className="text-xs text-muted-foreground">Standard applied rate</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5 space-y-2">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Building2 className="h-4 w-4 text-violet-600" />
                  <span className="text-sm font-medium">Shops</span>
                </div>
                <p className="text-2xl font-semibold">{shops.length}</p>
                <p className="text-xs text-muted-foreground">{activeShopsCount} active shop(s)</p>
              </CardContent>
            </Card>
          </div>

          {agentId ? (
            <OwnerWalletPnlCards ownerType="agent" ownerId={agentId} enabled={activeTab === 'overview'} />
          ) : null}
        </>
      )}

      {activeTab === 'hierarchy' && (
        <EntityHierarchyChart
          subtitle={`Parent super agent and ${shops.length} shop${shops.length === 1 ? '' : 's'} under this agent`}
          focus="agent"
          loadingParent={!!superAgentId && superAgentLoading}
          superAgent={
            superAgentDetail?.data?.superAgent
              ? {
                  id: superAgentDetail.data.superAgent.id,
                  name: superAgentDetail.data.superAgent.name,
                  email: superAgentDetail.data.superAgent.email,
                  status: superAgentDetail.data.superAgent.status,
                  to: `/super-agents/${superAgentDetail.data.superAgent.id}`,
                }
              : superAgentId
                ? { id: superAgentId, name: 'Super agent', to: `/super-agents/${superAgentId}` }
                : null
          }
          agent={{
            id: agent.id,
            name: agent.name,
            email: agent.email,
            status: agent.status,
            meta: agentCostRate != null ? `Cost ${(agentCostRate * 100).toFixed(2)}%` : undefined,
          }}
          shops={shops.map((shop) => ({
            id: shop.id,
            name: shop.name,
            email: shop.email,
            status: shop.status,
            meta: `Cost ${costAsPercent(shop)}`,
            to: `/shops/${shop.id}`,
          }))}
        />
      )}

      {canManage && activeTab === 'finance' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-1 border-b border-border/70 pb-3">
            <button
              type="button"
              onClick={() => setFinanceSection('transactions')}
              className={cn(
                'inline-flex items-center rounded-full px-3.5 py-1.5 text-[13px] transition-colors',
                financeSection === 'transactions'
                  ? 'bg-muted text-foreground font-medium'
                  : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground'
              )}
            >
              Transactions
            </button>
            <button
              type="button"
              onClick={() => setFinanceSection('wallet')}
              className={cn(
                'inline-flex items-center rounded-full px-3.5 py-1.5 text-[13px] transition-colors',
                financeSection === 'wallet'
                  ? 'bg-muted text-foreground font-medium'
                  : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground'
              )}
            >
              Wallet
            </button>
            <button
              type="button"
              onClick={() => setFinanceSection('cost-breakdown')}
              className={cn(
                'inline-flex items-center rounded-full px-3.5 py-1.5 text-[13px] transition-colors',
                financeSection === 'cost-breakdown'
                  ? 'bg-muted text-foreground font-medium'
                  : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground'
              )}
            >
              Cost breakdown
            </button>
          </div>

          {financeSection === 'transactions' && (
            <AgentPaymentTransactionsPanel
              agentId={agentId}
              queryEnabled
              showAdminTransactionsLink
            />
          )}

          {financeSection === 'wallet' && (
            <AgentWalletTransferPanel agentId={agentId} queryEnabled />
          )}
        </div>
      )}

      {canManage && activeTab === 'finance' && financeSection === 'cost-breakdown' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Cost breakdown</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 rounded-lg border bg-background/95 p-3 md:grid-cols-4 md:sticky md:top-4 md:z-10 backdrop-blur supports-[backdrop-filter]:bg-background/75">
              <Input type="date" value={reportFrom} onChange={(e) => setReportFrom(e.target.value)} />
              <Input type="date" value={reportTo} onChange={(e) => setReportTo(e.target.value)} />
              <select
                value={reportEventType}
                onChange={(e) => setReportEventType(e.target.value)}
                className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">All events</option>
                <option value="shop_direct_topup">Shop direct topup</option>
                <option value="user_direct_topup">User direct topup</option>
              </select>
              <Button type="button" variant="outline" onClick={() => void costReport.refetch()}>
                Apply
              </Button>
            </div>

            <div className="grid gap-3 md:grid-cols-4">
              <div className="rounded-md border p-3"><p className="text-xs text-muted-foreground">Rows</p><p className="text-lg font-semibold">{costReport.data?.data?.summary?.total_rows ?? 0}</p></div>
              <div className="rounded-md border p-3"><p className="text-xs text-muted-foreground">Gross</p><p className="text-lg font-semibold">${Number(costReport.data?.data?.summary?.total_gross_amount ?? 0).toFixed(2)}</p></div>
              <div className="rounded-md border p-3"><p className="text-xs text-muted-foreground">Shop cost</p><p className="text-lg font-semibold">${Number(costReport.data?.data?.summary?.total_shop_cost ?? 0).toFixed(2)}</p></div>
              <div className="rounded-md border p-3"><p className="text-xs text-muted-foreground">Agent margin</p><p className="text-lg font-semibold text-emerald-700">${Number(costReport.data?.data?.summary?.total_agent_margin ?? 0).toFixed(2)}</p></div>
            </div>

            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Shop</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">Credits</TableHead>
                    <TableHead className="text-right">Shop cost</TableHead>
                    <TableHead className="text-right">Agent margin</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {costReport.isLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="py-8">
                        <PageLoadingIndicator
                          variant="inline"
                          label="Loading..."
                          spinnerClassName="text-muted-foreground"
                          labelClassName="text-muted-foreground"
                        />
                      </TableCell>
                    </TableRow>
                  ) : (costReport.data?.data?.rows?.length ?? 0) === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-10 text-muted-foreground">
                        No cost data for current filters. Try a broader date range.
                      </TableCell>
                    </TableRow>
                  ) : (
                    costReport.data?.data?.rows?.map((row) => (
                      <TableRow key={`${row.payment_transaction_id}-${row.event_type}`}>
                        <TableCell>{formatInTimezone(row.transaction_at, timezone)}</TableCell>
                        <TableCell>{row.event_type === 'shop_direct_topup' ? 'Shop direct topup' : 'User direct topup'}</TableCell>
                        <TableCell>{row.shop_name}</TableCell>
                        <TableCell>{row.username || '—'}</TableCell>
                        <TableCell className="text-right">${Number(row.gross_amount).toFixed(2)}</TableCell>
                        <TableCell className="text-right">{Number(row.credit_amount).toFixed(2)}</TableCell>
                        <TableCell className="text-right">${Number(row.shop_cost_amount).toFixed(2)}</TableCell>
                        <TableCell className="text-right font-semibold text-emerald-700">${Number(row.agent_margin_amount).toFixed(2)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {canManage && activeTab === 'settings' && (
        <div className="space-y-4">
          {agent.status !== 'terminated' ? (
            <AdminPlayerWithdrawalAutoApproveCard
              level="agent"
              snapshot={agent.playerWithdrawalAutoApprove}
              saving={patchPlayerAutoApprove.isLoading}
              onSave={async (value) => {
                await patchPlayerAutoApprove.mutateAsync({
                  agentId: agentId!,
                  withdrawal_auto_approve_threshold: value,
                });
              }}
            />
          ) : null}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Account</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Email</span>
                <span className="font-medium">{agent.email}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Status</span>
                <Badge variant="outline" className={cn('uppercase text-xs tracking-wide', statusTone)}>
                  {agent.status}
                </Badge>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Phone</span>
                <span className="font-medium">{agent.phone || '—'}</span>
              </div>
              {agent.status !== 'terminated' && (
                <div className="pt-2 border-t flex flex-col sm:flex-row sm:flex-wrap gap-2">
                  <Button variant="outline" className="w-full sm:w-auto justify-center" onClick={() => setPasswordModalOpen(true)}>
                    <Key className="h-4 w-4 mr-2 shrink-0" />
                    Change password
                  </Button>
                  {canManageCredentials ? (
                    <Button variant="outline" className="w-full sm:w-auto justify-center" onClick={() => setUsernameModalOpen(true)}>
                      Change username
                    </Button>
                  ) : null}
                  {canUpdateContact ? (
                    <Button variant="outline" className="w-full sm:w-auto justify-center" onClick={() => setContactModalOpen(true)}>
                      <Mail className="h-4 w-4 mr-2 shrink-0" />
                      Update email / phone
                    </Button>
                  ) : null}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between gap-3">
                <span>Cost configuration</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCostRateModalOpen(true)}
                  disabled={patchAgentRates.isLoading}
                >
                  <Pencil className="h-4 w-4 mr-1.5" />
                  Edit
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-center justify-between gap-4">
                <span className="text-muted-foreground">Current cost rate</span>
                <span className="font-semibold">{costAsPercent(agent)}</span>
              </div>
              <p className="text-xs text-muted-foreground">Cost analytics and breakdown are available in the Finance tab.</p>
            </CardContent>
          </Card>

          {canManagePopupPermissions && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Bell className="h-4 w-4 text-primary" />
                  Popup / Broadcast permissions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="space-y-2 rounded-md border border-border/70 p-3">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-medium">Can send popups/broadcasts</p>
                      <p className="text-xs italic text-muted-foreground mt-0.5">
                        Agent portal can create delegated popups/broadcasts.
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!!agent.canCreateShopPopup}
                      onClick={() => {
                        if (!agentId || patchAgentPopup.isLoading) return;
                        void patchAgentPopup.mutateAsync({
                          agentId,
                          body: { can_create_shop_popup: !agent.canCreateShopPopup },
                        });
                      }}
                      disabled={patchAgentPopup.isLoading}
                      className={cn(
                        'relative mt-0.5 inline-flex h-6 w-11 shrink-0 rounded-full border transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                        agent.canCreateShopPopup ? 'bg-emerald-600 border-emerald-600' : 'bg-muted border-border',
                        patchAgentPopup.isLoading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                      )}
                    >
                      <span
                        className={cn(
                          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-[1px]',
                          agent.canCreateShopPopup ? 'translate-x-5' : 'translate-x-0.5'
                        )}
                      />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 rounded-md border border-border/70 p-3">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-medium">Can manage popup permissions of shops</p>
                      <p className="text-xs italic text-muted-foreground mt-0.5">
                        Agent can change popup permissions for each shop.
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!!agent.canManageShopPopupPermission}
                      onClick={() => {
                        if (!agentId || patchAgentPopup.isLoading) return;
                        void patchAgentPopup.mutateAsync({
                          agentId,
                          body: { can_manage_shop_popup_permission: !agent.canManageShopPopupPermission },
                        });
                      }}
                      disabled={patchAgentPopup.isLoading}
                      className={cn(
                        'relative mt-0.5 inline-flex h-6 w-11 shrink-0 rounded-full border transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                        agent.canManageShopPopupPermission
                          ? 'bg-emerald-600 border-emerald-600'
                          : 'bg-muted border-border',
                        patchAgentPopup.isLoading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                      )}
                    >
                      <span
                        className={cn(
                          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-[1px]',
                          agent.canManageShopPopupPermission ? 'translate-x-5' : 'translate-x-0.5'
                        )}
                      />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 rounded-md border border-border/70 p-3">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-medium">New shops inherit popup permission</p>
                      <p className="text-xs italic text-muted-foreground mt-0.5">Applies to newly created shops only.</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!!agent.defaultShopCanCreatePlayerPopup}
                      onClick={() => {
                        if (!agentId || patchAgentPopup.isLoading) return;
                        void patchAgentPopup.mutateAsync({
                          agentId,
                          body: {
                            default_shop_can_create_player_popup: !agent.defaultShopCanCreatePlayerPopup,
                          },
                        });
                      }}
                      disabled={patchAgentPopup.isLoading}
                      className={cn(
                        'relative mt-0.5 inline-flex h-6 w-11 shrink-0 rounded-full border transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                        agent.defaultShopCanCreatePlayerPopup
                          ? 'bg-emerald-600 border-emerald-600'
                          : 'bg-muted border-border',
                        patchAgentPopup.isLoading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                      )}
                    >
                      <span
                        className={cn(
                          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-[1px]',
                          agent.defaultShopCanCreatePlayerPopup ? 'translate-x-5' : 'translate-x-0.5'
                        )}
                      />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {canManage && agent.status !== 'terminated' ? (
            <Card className="border-destructive/40">
              <CardHeader>
                <CardTitle className="text-base text-destructive">Danger zone</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-medium">Terminate agent</p>
                  <p className="text-xs text-muted-foreground">
                    Permanently ends this agent after all shops are closed, funding debt is cleared, and no active
                    players remain.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => setTerminateModalOpen(true)}
                >
                  <Skull className="h-4 w-4 mr-1.5" aria-hidden />
                  Terminate agent
                </Button>
              </CardContent>
            </Card>
          ) : null}
        </div>
      )}

      {activeTab === 'shops' && (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Store className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">Shops</CardTitle>
            <span className="text-sm text-muted-foreground">({shops.length})</span>
          </div>
          {canManage && agent.status !== 'terminated' && (
            <Button size="sm" onClick={() => setShopModalOpen(true)}>
              <Plus className="h-4 w-4 mr-1.5" />
              New shop
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Cost</TableHead>
                  <TableHead>Status</TableHead>
                  {canManage && canManagePopupPermissions ? (
                    <TableHead className="w-[9rem]">Player popups/broadcasts</TableHead>
                  ) : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {shops.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={canManage && canManagePopupPermissions ? 5 : 4}
                      className="text-center py-10 text-muted-foreground"
                    >
                      No shops yet. Create one to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  shops.map((shop: CdnShop) => (
                    <TableRow key={shop.id}>
                      <TableCell>
                        <Link
                          to={`/shops/${shop.id}`}
                          className="font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                        >
                          {shop.name}
                        </Link>
                      </TableCell>
                      <TableCell>{shop.email}</TableCell>
                      <TableCell>{costAsPercent(shop)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={getStatusToneClass(shop.status)}>
                          {shop.status}
                        </Badge>
                      </TableCell>
                      {canManage && canManagePopupPermissions ? (
                        <TableCell>
                          <label className="inline-flex items-center gap-2 cursor-pointer text-xs">
                            <input
                              type="checkbox"
                              checked={!!shop.canCreatePlayerPopup}
                              onChange={(e) => {
                                if (!agentId) return;
                                void patchShopPopup.mutateAsync({
                                  agentId,
                                  shopId: shop.id,
                                  body: { can_create_player_popup: e.target.checked },
                                });
                              }}
                              disabled={patchShopPopup.isLoading || !agent.canCreateShopPopup}
                            />
                            <span className="text-muted-foreground">Allow</span>
                          </label>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      )}

      {canUpdateContact && agentId && agent.status !== 'terminated' ? (
        <AdminUpdateContactModal
          open={contactModalOpen}
          onClose={() => setContactModalOpen(false)}
          target={{ kind: 'agent', agentId }}
          currentEmail={agent.email}
          currentPhone={agent.phone}
          emailVerified={Boolean(
            (agent as { emailVerified?: boolean; email_verified?: boolean }).emailVerified ??
              (agent as { email_verified?: boolean }).email_verified
          )}
          phoneVerified={Boolean(
            (agent as { phoneVerified?: boolean; phone_verified?: boolean }).phoneVerified ??
              (agent as { phone_verified?: boolean }).phone_verified
          )}
          title="Update agent email / phone"
          onSuccess={() => {
            toast.success('Contact updated. Agent must re-verify.');
            void queryClient.invalidateQueries([QUERY_KEYS.ADMIN_AGENT_DETAIL, agentId]);
          }}
        />
      ) : null}

      {canManageCredentials && agentId && agent.status !== 'terminated' ? (
        <AdminUpdateUsernameModal
          open={usernameModalOpen}
          onClose={() => setUsernameModalOpen(false)}
          target={{ kind: 'agent', agentId }}
          currentUsername={(agent as { username?: string | null }).username}
          title="Change agent username"
          onSuccess={() => {
            toast.success('Username updated. Agent sessions were signed out.');
            void queryClient.invalidateQueries([QUERY_KEYS.ADMIN_AGENT_DETAIL, agentId]);
          }}
        />
      ) : null}

      {canManage && passwordModalOpen && agent.status !== 'terminated' && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="agent-password-modal-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) closePasswordModal();
          }}
        >
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle id="agent-password-modal-title" className="text-lg flex items-center gap-2">
                <Key className="h-5 w-5 text-primary" />
                Agent login password
              </CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={closePasswordModal}
                disabled={patchAgentPassword.isLoading}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Set a new password for this agent&apos;s portal login. All existing agent sessions are signed out after
                the change.
              </p>
              <form onSubmit={handleAgentPasswordSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="agent-new-password-modal" className="text-sm font-medium">
                    New password
                  </label>
                  <div className="relative">
                    <Input
                      id="agent-new-password-modal"
                      type={showAgentPasswordReset ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={agentPassword}
                      onChange={(ev) => setAgentPassword(ev.target.value)}
                      className="pr-10"
                      disabled={patchAgentPassword.isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowAgentPasswordReset((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label={showAgentPasswordReset ? 'Hide password' : 'Show password'}
                    >
                      {showAgentPasswordReset ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <label htmlFor="agent-confirm-password-modal" className="text-sm font-medium">
                    Confirm password
                  </label>
                  <Input
                    id="agent-confirm-password-modal"
                    type={showAgentPasswordReset ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={agentPasswordConfirm}
                    onChange={(ev) => setAgentPasswordConfirm(ev.target.value)}
                    disabled={patchAgentPassword.isLoading}
                  />
                </div>
                <p className="text-xs text-muted-foreground">At least {PASSWORD_MIN} characters.</p>
                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button type="button" variant="outline" onClick={closePasswordModal} disabled={patchAgentPassword.isLoading}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      patchAgentPassword.isLoading ||
                      agentPassword.length < PASSWORD_MIN ||
                      agentPasswordConfirm.length < PASSWORD_MIN
                    }
                  >
                    {patchAgentPassword.isLoading ? 'Updating…' : 'Update password'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {canManage && costRateModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="agent-cost-rate-modal-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !patchAgentRates.isLoading) setCostRateModalOpen(false);
          }}
        >
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle id="agent-cost-rate-modal-title" className="text-lg flex items-center gap-2">
                <Pencil className="h-5 w-5 text-primary" />
                Update cost rate
              </CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setCostRateModalOpen(false)}
                disabled={patchAgentRates.isLoading}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAgentCostRateSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="agent-cost-rate-input" className="text-sm font-medium">
                    Cost rate (%)
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="agent-cost-rate-input"
                      type="number"
                      step="0.01"
                      min={0}
                      max={100}
                      value={agentCostPercentInput}
                      onChange={(ev) => setAgentCostPercentInput(ev.target.value)}
                      disabled={patchAgentRates.isLoading}
                      className="max-w-[220px]"
                    />
                    <span className="text-muted-foreground">%</span>
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCostRateModalOpen(false)}
                    disabled={patchAgentRates.isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={patchAgentRates.isLoading || agentCostPercentInput.trim() === ''}
                  >
                    {patchAgentRates.isLoading ? 'Saving…' : 'Update'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {canManage && shopModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0"
          onClick={(e) => {
            if (e.target === e.currentTarget && !createShop.isLoading) {
              setShopModalOpen(false);
              resetShopForm();
            }
          }}
        >
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Store className="h-5 w-5 text-primary" />
                New shop
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShopModalOpen(false);
                  resetShopForm();
                }}
                disabled={createShop.isLoading}
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateShop} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Shop name</label>
                  <Input
                    required
                    value={shopForm.name}
                    onChange={(ev) => setShopForm((f) => ({ ...f, name: ev.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="agent-detail-create-shop-username">
                    User name
                  </label>
                  <Input
                    id="agent-detail-create-shop-username"
                    autoComplete="username"
                    placeholder="Login username (optional)"
                    value={shopForm.username}
                    onChange={(ev) => setShopForm((f) => ({ ...f, username: ev.target.value }))}
                    disabled={createShop.isLoading}
                  />
                  <p className="text-xs text-muted-foreground">
                    Optional. 3–80 chars: letters, numbers, dot, underscore, dash.
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Email</label>
                  <Input
                    required
                    type="email"
                    value={shopForm.email}
                    onChange={(ev) => setShopForm((f) => ({ ...f, email: ev.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="create-shop-phone">
                    Phone (optional)
                  </label>
                  <PhoneInput
                    id="create-shop-phone"
                    value={shopForm.phone}
                    onChange={(phone) => setShopForm((f) => ({ ...f, phone }))}
                    disabled={createShop.isLoading}
                    placeholder="Phone number"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Password</label>
                  <div className="relative">
                    <Input
                      required
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={shopForm.password}
                      onChange={(ev) => setShopForm((f) => ({ ...f, password: ev.target.value }))}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">At least {PASSWORD_MIN} characters.</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Cost rate (%)</label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        max={100}
                        value={shopForm.cost_percent}
                        onChange={(ev) => setShopForm((f) => ({ ...f, cost_percent: ev.target.value }))}
                        className="w-full"
                      />
                    </div>
                    <span className="text-sm text-muted-foreground shrink-0 font-medium">%</span>
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShopModalOpen(false);
                      resetShopForm();
                    }}
                    disabled={createShop.isLoading}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createShop.isLoading || shopForm.password.length < PASSWORD_MIN}>
                    {createShop.isLoading ? 'Creating…' : 'Create shop'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      <FinanceCorrectionDebitModal
        open={correctionOpen}
        onOpenChange={setCorrectionOpen}
        ownerType="agent"
        ownerId={agentId!}
        onApplied={() => {
          if (agentId) void queryClient.invalidateQueries(['adminAgentDetail', agentId]);
          void queryClient.invalidateQueries({ queryKey: ['usdWalletSummary'] });
        }}
      />

      <TerminateAgentModal
        agent={
          agent
            ? { id: agent.id, email: agent.email, name: agent.name }
            : null
        }
        isOpen={terminateModalOpen}
        onClose={() => setTerminateModalOpen(false)}
        onSuccess={() => navigate('/agents')}
      />

    </div>
  );
};

export default AgentDetailPage;
